/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { makeAutoObservable, runInAction } from 'mobx';

import { idpProjectService, tracingService } from 'services';
import { extractStructuredDocument } from 'App/Pages/Project/IdpApplication/idp-project/utils/extract-document';
import { notificationStore } from 'components/NotificationSystem';
import {
  getExpectedValues,
  getFieldsFromResults,
  getIncludedFieldsKeys,
  getNormalizedResults,
  getNormalizedUpdateResults,
  getRenameMappings,
  getIncludedFieldsIds
} from 'App/Pages/Project/IdpApplication/idp-project/utils/structured-extraction-helpers';
import {
  DEFAULT_DELIMITER,
  IDP_PROJECT_EXTRACTION_METHODS
} from 'App/Pages/Project/IdpApplication/idp-project/utils/constants';

/** @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').Table} Table */
/** @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').Field} Field */
/** @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').ExtractionField} ExtractionField */
/** @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').StructuredExtractionResults} StructuredExtractionResults */
/** @typedef {import('App/Pages/Project/IdpApplication/idp-project/utils/types').ExtractionResult} ExtractionResult */

export class StructuredExtractionStore {
  /** @type {StructuredExtractionResults} */
  structuredExtractionResults = null;
  /** @type {Array<string>} */
  #allFieldsIds = [];
  isExtracting = false;
  isUploading = false;
  documentForExtraction = null;

  testTemplateDocuments = [];
  testTemplateIsExtracting = false;
  testTemplateIncludedFieldsIds = [];
  testTemplateData = null;

  originalKeys = {};

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * @param {string} idpProjectId
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  init = async (idpProjectId, signal) => {
    await this.#fetchIdpDocuments(idpProjectId, signal);
  };

  reset = () => {
    runInAction(() => {
      this.structuredExtractionResults = null;
      this.documentForExtraction = null;
      this.isExtracting = false;
      this.isUploading = false;
      this.#allFieldsIds = [];
    });
  };

  /**
   * @param {Error} error
   * @param {Object} options
   * @param {string} [options.errorTitle]
   * @param {string} [options.errorMessage]
   * @param {string} options.tracingErrorMessage
   * @returns {null}
   */
  #handleExtractionError = (error, { errorTitle = 'Error', errorMessage, tracingErrorMessage }) => {
    notificationStore.error(
      {
        title: errorTitle,
        content:
          error?.message ??
          errorMessage ??
          'Please try again. If the problem persists, please get in touch with the support team.'
      },
      { shouldPersist: true }
    );

    tracingService.traceError(error, tracingErrorMessage);
    return null;
  };

  /**
   * Fetches all IDP documents for the given project (for initial load).
   * @param {string} idpProjectId
   * @param {AbortSignal} signal
   * @returns {Promise<Array>}
   */
  async #fetchIdpDocuments(idpProjectId, signal) {
    try {
      const documents = await idpProjectService.fetchIdpDocuments(idpProjectId, signal);
      runInAction(() => {
        const [documentForExtraction, ...testTemplateDocuments] = documents ?? [];
        this.documentForExtraction = documentForExtraction;
        this.testTemplateDocuments = testTemplateDocuments;
      });
    } catch (error) {
      this.#handleExtractionError(error, {
        errorTitle: 'Fetch documents failed',
        errorMessage: 'Failed to fetch documents. Please try again.',
        tracingErrorMessage: 'Failed to fetch IDP documents'
      });
      return [];
    }
  }

  /**
   * @param {string} id
   * @returns {Promise<Object>}
   */
  #uploadDocument = async (id) => {
    const doc = this.documentForExtraction?.clusterDocumentId === id ? this.documentForExtraction : null;
    return doc?.clusterDocumentId && doc?.clusterDocumentType
      ? Promise.resolve(doc)
      : await idpProjectService.uploadIdpDocumentToCluster(id);
  };

  #deleteDocument = async (documentId, onAfterDelete) => {
    try {
      runInAction(() => {
        this.isUploading = true;
      });

      await idpProjectService.deleteIdpDocument(documentId);

      await onAfterDelete?.();

      runInAction(() => {
        this.documentForExtraction = this.documentForExtraction?.id === documentId ? null : this.documentForExtraction;
        this.testTemplateDocuments = this.testTemplateDocuments.filter((doc) => doc.id !== documentId);
      });
    } catch (error) {
      this.#handleExtractionError(error, {
        errorTitle: 'Delete document failed',
        errorMessage: 'Failed to delete document. Please try again.',
        tracingErrorMessage: 'Failed to delete IDP document'
      });
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  };

  /**
   * @param {string} idpProjectId
   * @param {string} clusterId
   * @param {Array<string>} idpDocumentIds
   * @param {Object} extractOptions
   * @param {Array<string>} [extractOptions.includedFields=[]]
   * @param {Object<string,string>} [extractOptions.renameMappings]
   */
  #extractStructuredIdpDocuments = async (
    idpProjectId,
    clusterId,
    idpDocumentIds,
    extractOptions = { renameMappings: {}, includedFields: [] }
  ) => {
    try {
      const uploadedDocs = await Promise.all(idpDocumentIds.map((id) => this.#uploadDocument(id)));

      const results = await extractStructuredDocument({
        idpProjectId,
        clusterId,
        idpDocuments: uploadedDocs,
        delimiter: DEFAULT_DELIMITER,
        renameMappings: extractOptions.renameMappings,
        includedFields: extractOptions.includedFields
      });

      if (!results?.length || !uploadedDocs?.length || uploadedDocs.length !== results.length) {
        this.#handleExtractionError(new Error('No results or documents found'), {
          errorTitle: 'Extraction failed',
          errorMessage: 'No results or documents found. Please try again.',
          tracingErrorMessage: 'Failed to extract IDP documents'
        });
        return { results: [], uploadedDocs: [] };
      }

      return { results, uploadedDocs };
    } catch (error) {
      this.#handleExtractionError(error, {
        errorTitle: 'Extraction failed',
        errorMessage: 'Failed to extract IDP documents. Please try again.',
        tracingErrorMessage: 'Failed to extract IDP documents'
      });

      return { results: [], uploadedDocs: [] };
    } finally {
      runInAction(() => {
        this.isExtracting = false;
      });
    }
  };

  /**
   * @param {string} idpProjectId
   * @param {string} clusterId
   * @param {string} idpDocumentId
   */
  extractDocumentTemplate = async (idpProjectId, clusterId, idpDocumentId) => {
    runInAction(() => {
      this.isExtracting = true;
    });

    const { results, uploadedDocs } = await this.#extractStructuredIdpDocuments(idpProjectId, clusterId, [
      idpDocumentId
    ]);

    if (!results?.length) {
      runInAction(() => {
        this.isExtracting = true;
      });
      return;
    }

    const fields = getFieldsFromResults(results[0]);

    runInAction(() => {
      this.originalKeys = results[0].originalKeys ?? {};
    });

    try {
      const persistedFields = await idpProjectService.addStructuredExtractionFields(idpProjectId, fields);
      runInAction(() => {
        this.#allFieldsIds = persistedFields.map((/** @type ExtractionField */ f) => f.id);
      });

      const newValues = getExpectedValues(persistedFields, results[0]);

      const persistedResultsWithValues = await idpProjectService.updateIdpExtractionFieldValues(uploadedDocs[0]?.id, {
        newValues
      });

      runInAction(() => {
        this.structuredExtractionResults = getNormalizedResults(persistedResultsWithValues, this.originalKeys);
        this.documentForExtraction = uploadedDocs[0];
        this.isExtracting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.structuredExtractionResults = null;
        this.isExtracting = false;
      });

      this.#handleExtractionError(error, {
        errorTitle: 'Extraction failed',
        errorMessage: 'Failed to extract IDP document. Please try again.',
        tracingErrorMessage: 'Failed to extract IDP document'
      });
    } finally {
      runInAction(() => {
        this.isExtracting = false;
      });
    }
  };
  /**
   * @param {string} idpProjectId
   * @param {string} clusterId
   * @param {Array<string>} idpDocumentIds
   * @returns {Promise<void>}
   */
  extractTestTemplate = async (idpProjectId, clusterId, idpDocumentIds) => {
    runInAction(() => {
      this.testTemplateIsExtracting = true;
    });
    try {
      const includedFields = getIncludedFieldsKeys(
        this.testTemplateIncludedFieldsIds,
        this.structuredExtractionResults,
        this.originalKeys
      );
      const renameMappings = getRenameMappings(
        this.testTemplateIncludedFieldsIds,
        this.structuredExtractionResults,
        this.originalKeys
      );
      const { results, uploadedDocs } = await this.#extractStructuredIdpDocuments(
        idpProjectId,
        clusterId,
        idpDocumentIds,
        { includedFields, renameMappings }
      );
      if (!results?.length) {
        return;
      }
      const updatePromises = results.map(async (result, i) => {
        const doc = uploadedDocs[i];
        const persistedFields = await idpProjectService.fetchIdpExtractionFields(doc.id);
        const filteredFields = persistedFields.filter((f) => f.active);
        const newValues = getExpectedValues(filteredFields, result);
        const persistedResultsWithValues = await idpProjectService.updateIdpExtractionFieldValues(doc.id, {
          newValues
        });
        return getNormalizedResults(persistedResultsWithValues, this.originalKeys);
      });
      const normalizedResults = await Promise.all(updatePromises);
      runInAction(() => {
        this.testTemplateData = normalizedResults;
      });
    } catch (error) {
      this.#handleExtractionError(error, {
        errorTitle: 'Extraction failed',
        errorMessage: 'Failed to extract test template documents. Please try again.',
        tracingErrorMessage: 'Failed to extract test template documents'
      });
    } finally {
      runInAction(() => {
        this.testTemplateIsExtracting = false;
      });
    }
  };

  /**
   * @param {string} documentId
   * @returns {Promise<void>}
   */
  loadStructuredResultsForDocument = async (documentId) => {
    if (!documentId) {
      runInAction(() => {
        this.structuredExtractionResults = null;
      });
      return;
    }

    try {
      const results = await idpProjectService.fetchIdpExtractionFields(documentId);
      runInAction(() => {
        this.structuredExtractionResults = getNormalizedResults(results, this.originalKeys);
        this.#allFieldsIds = results.map((f) => f.id);
        this.testTemplateIncludedFieldsIds = getIncludedFieldsIds(this.structuredExtractionResults);
      });
    } catch (error) {
      runInAction(() => {
        this.structuredExtractionResults = null;
      });

      this.#handleExtractionError(error, {
        errorTitle: 'Load extraction results failed',
        errorMessage: 'Failed to load extraction results. Please try again.',
        tracingErrorMessage: 'Failed to load structured extraction results'
      });
    }
  };

  /**
   * Uploads documents to the given IDP project and refreshes the document list.
   * @param {string} idpProjectId
   * @param {File[]} files
   * @param {Function} [onDocumentsUploadProgress]
   * @returns {Promise<void>}
   */
  uploadDocumentForExtraction = async (idpProjectId, files, onDocumentsUploadProgress) => {
    try {
      runInAction(() => {
        this.isUploading = true;
      });

      await idpProjectService.uploadDocuments(idpProjectId, files, onDocumentsUploadProgress);
      await this.#fetchIdpDocuments(idpProjectId, undefined);
    } catch (error) {
      this.#handleExtractionError(error, {
        errorTitle: 'Document upload failed',
        errorMessage: 'Failed to upload document. Please try again.',
        tracingErrorMessage: 'Failed to upload IDP document'
      });
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  };

  /**
   * Deletes a document from the given IDP project and refreshes the document list.
   * @param {string} documentId
   * @returns {Promise<void>}
   */
  deleteDocumentForExtraction = async (documentId) => {
    await this.#deleteDocument(documentId, async () => {
      if (this.#allFieldsIds.length > 0) {
        // Delete all fields associated with the document
        const deleteFieldPromises = this.#allFieldsIds.map((fieldId) =>
          idpProjectService.deleteIdpExtractionField(fieldId).catch((fieldError) => {
            tracingService.traceError(fieldError, 'Failed to delete extraction field');
          })
        );

        await Promise.all(deleteFieldPromises);

        runInAction(() => {
          this.#allFieldsIds = [];
          this.structuredExtractionResults = null;
        });
      }
    });
  };

  /**
   * Deletes a document from the given IDP project and refreshes the document list.
   * @param {string} documentId
   * @returns {Promise<void>}
   */
  deleteTestDocument = async (documentId) => {
    await this.#deleteDocument(documentId);
  };

  /**
   * Updates a single extraction field by id using the idpProjectService.updateIdpExtractionField method.
   * @param {string} fieldId - The ID of the extraction field to update
   * @param {ExtractionField} updatedField
   * @returns {Promise<void>}
   */
  updateExtractionField = async (fieldId, updatedField) => {
    try {
      const updatePromise = await idpProjectService.updateIdpExtractionField({
        id: fieldId,
        name: updatedField.name,
        extractionMethod: IDP_PROJECT_EXTRACTION_METHODS[0].value,
        replacedName: updatedField.replacedName,
        confidence: updatedField.confidence,
        active: updatedField.active
      });

      runInAction(() => {
        this.structuredExtractionResults = getNormalizedUpdateResults(this.structuredExtractionResults, updatedField);
        this.testTemplateIncludedFieldsIds = getIncludedFieldsIds(this.structuredExtractionResults);
      });

      return updatePromise;
    } catch (error) {
      this.#handleExtractionError(error, {
        errorTitle: 'Update field failed',
        errorMessage: 'Failed to update extraction field. Please try again.',
        tracingErrorMessage: 'Failed to update extraction field'
      });
      return;
    }
  };

  /**
   * Bulk updates extraction fields: calls updateIdpExtractionField for each field with its own updated data.
   * @param {Array<ExtractionField>} updatedFields
   * @returns {Promise<void>}
   */
  bulkUpdateExtractionFields = async (updatedFields) => {
    const updatePromises = updatedFields.map((field) => this.updateExtractionField(field.id, field));
    await Promise.all(updatePromises);

    notificationStore.success({
      title: 'Update successful',
      content: 'Extraction fields have been updated successfully.'
    });
  };
}

export default new StructuredExtractionStore();
